import { Injectable, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ENUM_ActivityEventType } from '@api-new/common';
import { HTTP_CP_RecordActivityEvent_Request_ActivityEvent, ReportingApiService } from '@api-new/reportingservice';
import { forceSentAnalyticsEventsInBuffer, logAnalyticsEvent, sendLogEvents } from '@client-ui/store/analytics/analytics.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { routerNavigatedAction } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { paths } from '@platform/paths';
import { selectIsLoadingPortfolio } from '@platform/portfolio/store/portfolio.selectors';
import { AnalyticsService } from '@shared/services/analytics.service';
import { selectClientUserAnalyticsEventMetadata } from '@shared/store/analytics/analytics.selects';
import { selectClientUser } from '@shared/store/client-user/client-user.selectors';
import dayjs from 'dayjs';
import { concatMap, delay, merge, of, timer } from 'rxjs';
import { buffer, catchError, filter, map, switchMap, take, timeout, withLatestFrom } from 'rxjs/operators';
import * as uuid from 'uuid';

const BUFFER_TIME = 30000; // (30sec) Buffer time over which events are collected for before being sent to BE

@Injectable()
export class AnalyticsEffects {
  private actions$ = inject(Actions);
  private globalStore = inject(Store);
  private analyticsService = inject(AnalyticsService);
  private reportingApiService = inject(ReportingApiService);
  private title = inject(Title);

  trackRouterChanges$ = createEffect(() =>
    this.actions$.pipe(
      ofType(routerNavigatedAction),
      delay(0), // delay is necessary to avoid problems with sending old title after routerNavigated
      map(({ payload }) =>
        logAnalyticsEvent({
          event: {
            type: ENUM_ActivityEventType.ACTIVITY_EVENT_TYPE_ROUTE,
            route: location.origin + payload.event.urlAfterRedirects,
            name: this.title.getTitle(),
          },
        }),
      ),
    ),
  );

  bufferAndSendEvents$ = createEffect(() => {
    const flushSignal$ = this.actions$.pipe(ofType(forceSentAnalyticsEventsInBuffer));
    const timer$ = timer(BUFFER_TIME, BUFFER_TIME);

    const closingNotifier$ = merge(flushSignal$, timer$);

    return this.actions$.pipe(
      ofType(logAnalyticsEvent),
      withLatestFrom(this.globalStore.select(selectClientUser)),
      concatMap(([{ event }, isSignedIn]) => {
        event.route = event.route ?? location.href;
        if (isSignedIn && event.route.includes(`/${paths.PLATFORM}`)) {
          return this.globalStore.select(selectIsLoadingPortfolio).pipe(
            filter((isLoading) => !isLoading), // Wait until isLoading is false
            take(1),
            timeout(5000),
            catchError(() => of(true)),
            map(() => ({ event })), // Pass the original event data through
          );
        }
        return of({ event });
      }),
      withLatestFrom(this.globalStore.select(selectClientUserAnalyticsEventMetadata)),
      map(([{ event }, metadata]) => {
        const sessionData = this.analyticsService.getSessionData();
        return {
          ...event,
          eventId: uuid.v4(),
          clientUserId: metadata?.clientUserId,
          companyId: metadata?.companyId,
          ...(metadata && {
            eventData: {
              isFirstTimeLogin: sessionData.isFirstSignIn,
              isLandlord: metadata.isLandlord,
              ...(event.route.includes('/platform') && {
                hasBetterDeal: metadata.hasBetterDeal,
                hasActiveMortgageCase: metadata.hasActiveMortgageCase,
              }),
            },
          }),
          route: event.route ?? location.href,
          trafficSource: sessionData.trafficSource,
          timestamp: dayjs().toISOString(),
          sessionId: sessionData.sessionId,
          device: navigator.userAgent,
        } as HTTP_CP_RecordActivityEvent_Request_ActivityEvent;
      }),
      buffer(closingNotifier$),
      filter((actions) => actions.length > 0),
      map((events) => sendLogEvents({ events })),
    );
  });

  sendEventsToServer$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(sendLogEvents),
        switchMap(({ events }) => this.reportingApiService.HTTP_CP_RecordActivityEvent({ activityEvents: events }, { keepalive: true })),
      ),
    {
      dispatch: false,
    },
  );
}
